import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

/**
 * FullWidthHero - Hero section avec image en fond plein écran
 * 
 * Image en background, texte superposé avec overlay pour lisibilité
 * Style sobre, premium, professionnel
 * 
 * @param {string} image - URL de l'image de fond
 * @param {string} imageAlt - Texte alternatif de l'image (pour accessibilité)
 * @param {string} badge - Texte du badge (optionnel)
 * @param {React.Component} badgeIcon - Icône du badge (optionnel)
 * @param {string} title - Titre principal
 * @param {string} titleHighlight - Partie du titre en surbrillance (optionnel)
 * @param {string} subtitle - Sous-titre/accroche
 * @param {object} primaryCta - { text, href, icon } pour le CTA principal
 * @param {object} secondaryCta - { text, href } pour le CTA secondaire (optionnel)
 * @param {string} variant - 'home' | 'page' | 'compact'
 * @param {string} overlayPosition - 'center' | 'left' | 'right' - position du contenu
 */
const FullWidthHero = ({
  image,
  imageAlt,
  badge,
  badgeIcon: BadgeIcon,
  title,
  titleHighlight,
  subtitle,
  primaryCta,
  secondaryCta,
  variant = 'page',
  overlayPosition = 'left',
  className = ''
}) => {
  const isHome = variant === 'home';
  const isCompact = variant === 'compact';

  // Hauteur selon la variante
  const heightClass = isHome 
    ? 'min-h-[85vh]' 
    : isCompact 
      ? 'min-h-[45vh] md:min-h-[50vh]' 
      : 'min-h-[55vh] md:min-h-[60vh]';

  // Position du contenu
  const contentPositionClass = {
    center: 'items-center text-center',
    left: 'items-start text-left',
    right: 'items-end text-right'
  }[overlayPosition];

  const contentMaxWidth = overlayPosition === 'center' ? 'max-w-3xl mx-auto' : 'max-w-2xl';

  return (
    <section 
      className={`relative ${heightClass} flex items-center overflow-hidden ${className}`}
      data-testid="fullwidth-hero"
    >
      {/* Background Image - Full width, spa positioned on right for text on left */}
      <div className="absolute inset-0">
        <img 
          src={image}
          alt={imageAlt}
          className="w-full h-full object-cover"
          style={{ objectPosition: '65% 55%' }}
          loading="eager"
        />
      </div>

      {/* Overlay gradient for text readability */}
      <div 
        className={`absolute inset-0 ${
          overlayPosition === 'left' 
            ? 'bg-gradient-to-r from-[#0F172A]/85 via-[#0F172A]/60 to-transparent'
            : overlayPosition === 'right'
              ? 'bg-gradient-to-l from-[#0F172A]/85 via-[#0F172A]/60 to-transparent'
              : 'bg-[#0F172A]/60'
        }`}
      />

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`flex flex-col ${contentPositionClass} ${contentMaxWidth}`}>
          
          {/* Badge */}
          {badge && (
            <div className="inline-flex items-center gap-2 mb-5">
              {BadgeIcon && <BadgeIcon className="w-5 h-5 text-[#F97316]" />}
              <span className="text-[#F97316] font-semibold text-sm uppercase tracking-wider">
                {badge}
              </span>
            </div>
          )}

          {/* Title */}
          <h1 className={`font-bold text-white leading-tight mb-5 ${
            isHome 
              ? 'text-3xl sm:text-4xl lg:text-5xl xl:text-6xl' 
              : isCompact
                ? 'text-2xl sm:text-3xl lg:text-4xl'
                : 'text-2xl sm:text-3xl lg:text-4xl xl:text-5xl'
          }`} style={{ fontFamily: 'Manrope, sans-serif' }}>
            {title}
            {titleHighlight && (
              <>
                {' '}
                <span className="text-[#F97316]">{titleHighlight}</span>
              </>
            )}
          </h1>

          {/* Subtitle */}
          {subtitle && (
            <p className={`text-slate-200 leading-relaxed mb-8 ${
              isHome 
                ? 'text-lg lg:text-xl' 
                : isCompact
                  ? 'text-base'
                  : 'text-base lg:text-lg'
            }`}>
              {subtitle}
            </p>
          )}

          {/* CTAs */}
          {(primaryCta || secondaryCta) && (
            <div className={`flex flex-col sm:flex-row gap-4 ${
              overlayPosition === 'center' ? 'justify-center' : ''
            }`}>
              {primaryCta && (
                <Link to={primaryCta.href}>
                  <Button 
                    size={isCompact ? 'default' : 'lg'} 
                    className={`bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold w-full sm:w-auto rounded-lg transition-all hover:-translate-y-0.5 shadow-lg ${
                      isCompact ? 'h-11 px-6' : 'h-14 px-8'
                    }`}
                  >
                    {primaryCta.text}
                    {primaryCta.icon ? (
                      <primaryCta.icon className="w-5 h-5 ml-2" />
                    ) : (
                      <ArrowRight className="w-5 h-5 ml-2" />
                    )}
                  </Button>
                </Link>
              )}
              {secondaryCta && (
                <Link to={secondaryCta.href}>
                  <Button 
                    size={isCompact ? 'default' : 'lg'} 
                    variant="outline" 
                    className={`bg-white/10 backdrop-blur-sm border-white/30 text-white hover:bg-white/20 w-full sm:w-auto rounded-lg ${
                      isCompact ? 'h-11' : 'h-14'
                    }`}
                  >
                    {secondaryCta.text}
                  </Button>
                </Link>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Bottom wave for smooth transition (only on home variant) */}
      {isHome && (
        <div className="absolute bottom-0 left-0 right-0 z-20">
          <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 60V40C240 55 480 25 720 40C960 55 1200 25 1440 40V60H0Z" className="fill-slate-50"/>
          </svg>
        </div>
      )}
    </section>
  );
};

export default FullWidthHero;
